import { useState } from "react";
import parsePhoneNumber, { AsYouType as PhoneNumberFormatterAsYouType } from 'libphonenumber-js';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Combobox } from "@/components/ui/combobox";
import { PdfField } from "../types";
import { useListCustomers } from "@/lib/api/customer";
import { Customer } from "@/lib/api/reports";
import { Users } from "lucide-react";

interface PrepopulateDialogProps {
  fields: PdfField[];
  onUpdateField: (fieldId: string, updates: Partial<PdfField>) => void;
}

export function PrepopulateDialog({ fields, onUpdateField }: PrepopulateDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");

  const customersResult = useListCustomers();
  const customers = customersResult?.data?.data || [];

  const selectedCustomer = customers.find((c: { id: string }) => c.id === selectedCustomerId);

  const customerOptions = customers.map((customer: { id: string; long_name: string; short_name: string }) => ({
    value: customer.id,
    label: customer.long_name || customer.short_name,
  }));

  const mapCustomerDataToFields = (customer: Customer): Record<string, string> => {
    const fieldMapping: Record<string, string> = {};

    // Map customer_name
    if (customer.long_name) {
      fieldMapping.customer_name = customer.long_name;
    }

    // Map customer_address - combine address components
    const addressParts = [
      customer.address_line_one,
      customer.address_line_two,
      customer.city,
      customer.state,
      customer.zipcode,
    ].filter(Boolean);

    if (addressParts.length > 0) {
      fieldMapping.customer_address = addressParts.join(' ');
    }

    if (customer.email) {
      fieldMapping.customer_email = customer.email;
    }

    if (customer.phone_number) {
      fieldMapping.customer_phone = parsePhoneNumber(customer.phone_number)?.formatNational() || customer.phone_number || "";
    }

    if (customer.gps_latitude && customer.gps_longitude) {
      fieldMapping.customer_gps_coordinates = `(${customer.gps_latitude}, ${customer.gps_longitude})`;
    }

    return fieldMapping;
  };

  const handlePopulateFields = () => {
    if (!selectedCustomer) return;

    const customerData = mapCustomerDataToFields(selectedCustomer);

    // Find all system reference fields that match customer field IDs
    const customerFields = fields.filter(field =>
      field.field_type === 'system' &&
      Object.keys(customerData).includes(field.field_id)
    );

    // Update each matching field with customer data
    customerFields.forEach(field => {
      const customerValue = customerData[field.field_id];
      if (customerValue) {
        onUpdateField(field.field_id, { value: customerValue });
      }
    });

    setOpen(false);
    setSelectedCustomerId("");
  };

  const getPopulatableFieldsCount = () => {
    if (!selectedCustomer) return 0;

    const customerData = mapCustomerDataToFields(selectedCustomer);
    return fields.filter(field =>
      field.field_type === 'system' &&
      Object.keys(customerData).includes(field.field_id)
    ).length;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Users className="w-4 h-4 mr-1" />
          Add Sample Data
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md md:min-w-[600px]">
        <DialogHeader>
          <DialogTitle>Preview Customer Data</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              Select Customer
            </label>
            <Combobox
              className="min-w-[400px]"
              popoverClassName="min-w-[400px]"
              placeholder={customersResult.isLoading ? "Loading customers..." : "Select a customer..."}
              options={customerOptions}
              value={selectedCustomerId}
              onValueChange={setSelectedCustomerId}
              disabled={customersResult.isLoading}
              virtualizeList
              dwimOnChange
              isModal
            />
          </div>

          {selectedCustomer && (
            <div className="bg-gray-50 p-3 rounded-lg space-y-2">
              <p className="text-sm font-medium text-gray-700">Preview:</p>
              <div className="space-y-1 text-xs md:grid md:grid-cols-2">
                {selectedCustomer.long_name && (
                  <div><span className="font-medium">Name:</span> {selectedCustomer.long_name}</div>
                )}
                {selectedCustomer.email && (
                  <div><span className="font-medium">Email:</span> {selectedCustomer.email}</div>
                )}
                {selectedCustomer.phone_number && (
                  <div><span className="font-medium">Phone:</span> {selectedCustomer.phone_number || ""}</div>
                )}
                {selectedCustomer.gps_latitude && selectedCustomer.gps_longitude && (
                  <div><span className="font-medium">GPS:</span> ({selectedCustomer.gps_latitude}, {selectedCustomer.gps_longitude})</div>
                )}
                {(selectedCustomer.address_line_one || selectedCustomer.city) && (
                  <div>
                    <span className="font-medium">Address:</span> {
                      [selectedCustomer.address_line_one, selectedCustomer.city, selectedCustomer.state, selectedCustomer.zipcode]
                        .filter(Boolean).join(' ')
                    }
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-600 mt-2">
                {getPopulatableFieldsCount()} field(s) will be populated
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false);
              setSelectedCustomerId("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePopulateFields}
            disabled={!selectedCustomer || getPopulatableFieldsCount() === 0}
          >
            Populate Fields
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
